import React, { useEffect, useState } from "react";
import { ClipboardCheck, CheckCircle2, ShoppingBag, Layers } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, fmtUnit, orderLabel } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useT } from "../lib/i18n";

const CHK_TONE = { AVAILABLE: "success", PARTIAL: "warn", UNAVAILABLE: "danger" };

export default function Purchasing() {
  const toast = useToast();
  const { t } = useT();
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState(null);
  const [checklistOrder, setChecklistOrder] = useState(null);
  const [chk, setChk] = useState({});
  const [productMap, setProductMap] = useState({});

  const load = () => {
    api.get("/purchasing/summary").then(({ data }) => setSummary(data));
    api.get("/orders").then(({ data }) => setOrders(data.filter((o) => ["SUBMITTED", "APPROVED"].includes(o.status))));
  };
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => {
      const m = {};
      data.forEach((p) => { m[p.id] = p; });
      setProductMap(m);
    });
    // eslint-disable-next-line
  }, []);

  if (!summary || !orders) return <PageLoader />;

  const approve = async (o) => {
    try { await api.post(`/orders/${o.id}/approve`); toast.success(`${o.order_no} ${t("approved")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };
  const purchase = async (o) => {
    try { await api.post(`/orders/${o.id}/purchase`); toast.success(`${o.order_no} ${t("marked purchased")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };
  const openChecklist = (o) => {
    const c = {};
    o.items.forEach((it) => { c[it.product_id] = it.checklist_status || "AVAILABLE"; });
    setChk(c); setChecklistOrder(o);
  };
  const saveChecklist = async () => {
    const items = Object.entries(chk).map(([product_id, status]) => ({ product_id, status }));
    await api.post(`/orders/${checklistOrder.id}/checklist`, items);
    toast.success(t("Checklist saved")); setChecklistOrder(null); load();
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Purchasing")} subtitle={t("Aggregated demand & order approvals")} icon={ClipboardCheck} />

      <div className="mb-5 flex gap-2">
        <button onClick={() => setTab("summary")} data-testid="tab-summary"
          className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${tab === "summary" ? "bg-primary text-white" : "bg-white border border-line text-ink-soft"}`}>
          <Layers size={16} /> {t("Purchasing Summary")}
        </button>
        <button onClick={() => setTab("orders")} data-testid="tab-orders"
          className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${tab === "orders" ? "bg-primary text-white" : "bg-white border border-line text-ink-soft"}`}>
          <ShoppingBag size={16} /> {t("Orders Pipeline")} ({orders.length})
        </button>
      </div>

      {tab === "summary" ? (
        <Card className="overflow-hidden p-0">
          <div className="border-b border-line px-5 py-4">
            <h3 className="font-heading text-lg font-bold">{t("Aggregated Demand")}</h3>
            <p className="text-sm text-ink-soft">{t("Across")} {summary.order_count} {t("submitted/approved orders")}</p>
          </div>
          {summary.items.length === 0 ? (
            <p className="px-5 py-10 text-center text-ink-mute">{t("No pending demand. All caught up!")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-5 py-3">{t("Product")}</th><th className="px-5 py-3 text-right">{t("Total Quantity")}</th>
                <th className="px-5 py-3">{t("Unit")}</th><th className="px-5 py-3 text-right">{t("Branches")}</th></tr></thead>
              <tbody>
                {summary.items.map((it) => {
                  const p = productMap[it.product_id] || {};
                  return (
                  <tr key={it.product_id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`summary-row-${it.product_name}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <ProductAvatar image={p.image} colorKey={p.category_color} name={it.product_name} size="sm" />
                        <span className="font-semibold text-ink">{it.product_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-primary">{fmtNum(it.total_quantity)}</td>
                    <td className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-ink-mute">{fmtUnit(it.unit)}</td>
                    <td className="px-5 py-3 text-right text-ink-soft">{it.branches.length}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {orders.length === 0 && <p className="text-ink-mute">{t("No orders awaiting action.")}</p>}
          {orders.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`pipeline-${o.order_no}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-bold">{orderLabel(o)}</p>
                  <p className="text-xs text-ink-mute">{o.order_no} · {o.items.length} {t("items")}</p>
                </div>
                <Badge tone={o.status === "SUBMITTED" ? "warn" : "info"}>{t(o.status)}</Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                <Button variant="outline" size="sm" onClick={() => openChecklist(o)} data-testid={`checklist-${o.order_no}`}>{t("Checklist")}</Button>
                {o.status === "SUBMITTED" && <Button size="sm" onClick={() => approve(o)} data-testid={`approve-${o.order_no}`}><CheckCircle2 size={15} /> {t("Approve")}</Button>}
                {o.status === "APPROVED" && <Button size="sm" variant="success" onClick={() => purchase(o)} data-testid={`purchase-${o.order_no}`}><ShoppingBag size={15} /> {t("Mark Purchased")}</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!checklistOrder} onClose={() => setChecklistOrder(null)} title={`${t("Checklist")} · ${checklistOrder?.order_no}`}
        footer={<><Button variant="outline" onClick={() => setChecklistOrder(null)}>{t("Cancel")}</Button><Button onClick={saveChecklist} data-testid="save-checklist">{t("Save")}</Button></>}>
        <div className="space-y-2">
          {checklistOrder?.items.map((it) => (
            <div key={it.product_id} className="flex items-center justify-between gap-2">
              <span className="flex-1 text-sm font-medium">{it.product_name} <span className="text-ink-mute">({fmtNum(it.ordered_quantity)} {it.unit})</span></span>
              <Select value={chk[it.product_id]} onChange={(e) => setChk((c) => ({ ...c, [it.product_id]: e.target.value }))} className="w-36">
                <option value="AVAILABLE">{t("Available")}</option>
                <option value="PARTIAL">{t("Partial")}</option>
                <option value="UNAVAILABLE">{t("Unavailable")}</option>
              </Select>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
